// OneDriveBrowserModal — pick a file from the advisor's personal
// OneDrive and copy it onto a Stewardship record (household /
// contact / transaction / etc) as a Stewardship-managed document.
//
// CONSUMER: internal-ui. Mounted from DocumentsTab.
//
// v1.x phase 11.16 (Tier B item 13). Per Grant's "Option A"
// decision on 2026-05-02: copy semantics, not link. Once copied
// the file lives in SharePoint as a Stewardship document. The
// original stays untouched in OneDrive.
//
// UX:
//   * Modal opens at OneDrive root.
//   * Folders show a folder icon; click to navigate. Breadcrumbs
//     across the top track navigation depth.
//   * Files show a file icon + size + last-modified. Click to
//     select; "Copy" button activates.
//   * Search box (top-right) flips to search mode and lists matches
//     across the whole OneDrive.
//   * Pagination via "Load more" when nextLink is present.
//   * 4 MB cap surfaced inline so users don't try to copy a 50-MB
//     scan and get a server error after 30 seconds.

import { useCallback, useEffect, useState } from "react";
import { FileText, Folder } from "lucide-react";

import {
  apiV11,
  ApiError,
  type DocumentRec,
  type OneDriveCopyEntityType,
  type OneDriveItem,
} from "../api";
import { Modal } from "./Modal";
import { Button, ErrorBanner } from "../ui";

const FOUR_MB = 4 * 1024 * 1024;

function humanFileSize(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatModified(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// A single navigation level — used to render breadcrumbs.
interface NavCrumb {
  folderId: string | null; // null = root
  label: string;
}

const ROOT_CRUMB: NavCrumb = { folderId: null, label: "OneDrive" };

interface State {
  loading: boolean;
  error: string | null;
  items: OneDriveItem[];
  nextLink: string | null;
  mode: "browse" | "search";
}

const INITIAL: State = {
  loading: false,
  error: null,
  items: [],
  nextLink: null,
  mode: "browse",
};

// Two things this modal does, sharing one browser.
//
//   copy — the original: land the chosen file on a record as a
//          Stewardship-managed document (DocumentsTab).
//   pick — added 2026-08-20 for the email composer's Attach menu:
//          hand the chosen item back and let the caller decide. The
//          composer attaches it by reference and the bytes never touch
//          the browser, so nothing gets copied into SharePoint just
//          because someone emailed it.
export type OneDriveBrowserModalProps = {
  open: boolean;
  onClose: () => void;
} & (
  | {
      mode?: "copy";
      // Where the copied file should land. The modal posts to
      // /api/files/onedrive/copy-to-entity with these fields.
      entityType: OneDriveCopyEntityType;
      entityId: string;
      // Called when a file copy succeeds. Parent prepends the doc to
      // its list and closes the modal.
      onCopied: (doc: DocumentRec) => void;
      onPick?: never;
      // Size ceiling to enforce, in bytes. Defaults to the 4 MB
      // SharePoint upload cap.
      maxBytes?: number;
    }
  | {
      mode: "pick";
      onPick: (item: OneDriveItem) => void;
      entityType?: never;
      entityId?: never;
      onCopied?: never;
      maxBytes?: number;
    }
);

export function OneDriveBrowserModal(props: OneDriveBrowserModalProps) {
  const { open, onClose } = props;
  const isPick = props.mode === "pick";
  const maxBytes = props.maxBytes ?? FOUR_MB;
  const [state, setState] = useState<State>(INITIAL);
  const [crumbs, setCrumbs] = useState<NavCrumb[]>([ROOT_CRUMB]);
  const [selected, setSelected] = useState<OneDriveItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  // Reset every time the modal opens. Keeps state from leaking
  // between sessions.
  useEffect(() => {
    if (!open) return;
    setState(INITIAL);
    setCrumbs([ROOT_CRUMB]);
    setSelected(null);
    setSearchQuery("");
    setCopying(false);
    setCopyError(null);
    void loadItems(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadItems = useCallback(
    async (folderId: string | null, opts: { append?: boolean } = {}) => {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        ...(opts.append ? {} : { items: [], nextLink: null, mode: "browse" }),
      }));
      try {
        const resp = await apiV11.browseOneDrive({ folderId });
        setState((prev) => ({
          loading: false,
          error: null,
          items: opts.append ? [...prev.items, ...resp.items] : resp.items,
          nextLink: resp.nextLink,
          mode: resp.mode,
        }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof ApiError ? err.message : "Couldn't load OneDrive.",
        }));
      }
    },
    [],
  );

  const loadMore = useCallback(async () => {
    if (!state.nextLink) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const resp = await apiV11.browseOneDrive({ next: state.nextLink });
      setState((prev) => ({
        loading: false,
        error: null,
        items: [...prev.items, ...resp.items],
        nextLink: resp.nextLink,
        mode: resp.mode,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof ApiError ? err.message : "Couldn't load OneDrive.",
      }));
    }
  }, [state.nextLink]);

  const runSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (q.length === 0) {
      // Empty search → reset to current breadcrumb level.
      const lastCrumb = crumbs[crumbs.length - 1] ?? ROOT_CRUMB;
      setSelected(null);
      void loadItems(lastCrumb.folderId);
      return;
    }
    setState({ loading: true, error: null, items: [], nextLink: null, mode: "search" });
    setSelected(null);
    try {
      const resp = await apiV11.browseOneDrive({ q });
      setState({
        loading: false,
        error: null,
        items: resp.items,
        nextLink: null,
        mode: "search",
      });
    } catch (err) {
      setState({
        loading: false,
        error: err instanceof ApiError ? err.message : "Search failed.",
        items: [],
        nextLink: null,
        mode: "search",
      });
    }
  }, [searchQuery, crumbs, loadItems]);

  const navigateInto = useCallback(
    (item: OneDriveItem) => {
      if (!item.isFolder) {
        setSelected(item);
        return;
      }
      // Walking deeper. Append the new crumb.
      const nextCrumbs: NavCrumb[] = [
        ...crumbs,
        { folderId: item.id, label: item.name },
      ];
      setCrumbs(nextCrumbs);
      setSelected(null);
      setSearchQuery("");
      void loadItems(item.id);
    },
    [crumbs, loadItems],
  );

  const navigateToCrumb = useCallback(
    (index: number) => {
      const cropped = crumbs.slice(0, index + 1);
      setCrumbs(cropped);
      setSelected(null);
      setSearchQuery("");
      const last = cropped[cropped.length - 1] ?? ROOT_CRUMB;
      void loadItems(last.folderId);
    },
    [crumbs, loadItems],
  );

  const confirmSelection = useCallback(async () => {
    if (!selected || selected.isFolder) return;
    if (typeof selected.size === "number" && selected.size > maxBytes) {
      setCopyError(
        `That file is ${humanFileSize(selected.size)}. ${humanFileSize(maxBytes)} max.`,
      );
      return;
    }
    // Pick mode hands the item back untouched — no copy, no upload.
    if (props.mode === "pick") {
      props.onPick(selected);
      onClose();
      return;
    }
    setCopying(true);
    setCopyError(null);
    try {
      const resp = await apiV11.copyOneDriveItemToEntity({
        onedrive_item_id: selected.id,
        entity_type: props.entityType,
        entity_id: props.entityId,
      });
      props.onCopied(resp.document);
      onClose();
    } catch (err) {
      setCopyError(err instanceof ApiError ? err.message : "Copy failed.");
    } finally {
      setCopying(false);
    }
  }, [selected, maxBytes, props, onClose]);

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!copying) onClose();
      }}
      title={isPick ? "Attach from OneDrive" : "Copy from OneDrive"}
      className="onedrive-browser-modal"
      data-testid="onedrive-browser-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={copying}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void confirmSelection()}
            disabled={
              copying ||
              !selected ||
              selected.isFolder ||
              (typeof selected.size === "number" && selected.size > maxBytes)
            }
          >
            {copying ? "Copying…" : isPick ? "Attach" : "Copy here"}
          </Button>
        </>
      }
    >
      <div className="onedrive-browser-toolbar">
        <nav className="onedrive-browser-crumbs" aria-label="OneDrive breadcrumbs">
          {crumbs.map((c, i) => (
            <span key={`${c.folderId ?? "root"}-${i}`}>
              {i > 0 ? <span className="onedrive-browser-crumb-sep"> / </span> : null}
              <button
                type="button"
                className="onedrive-browser-crumb"
                onClick={() => navigateToCrumb(i)}
                disabled={i === crumbs.length - 1 && state.mode !== "search"}
              >
                {c.label}
              </button>
            </span>
          ))}
        </nav>
        <form
          className="onedrive-browser-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            void runSearch();
          }}
        >
          <input
            type="search"
            placeholder="Search OneDrive…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search OneDrive"
          />
          <Button variant="ghost" type="submit" disabled={state.loading}>
            Search
          </Button>
        </form>
      </div>

      {state.error ? <ErrorBanner message={state.error} /> : null}
      {copyError ? <ErrorBanner message={copyError} /> : null}

      {state.mode === "search" ? (
        <p className="onedrive-browser-mode-note">
          Showing search results. Click "OneDrive" in the breadcrumbs to
          return to browse.
        </p>
      ) : null}

      <div className="onedrive-browser-list" role="listbox" aria-label="OneDrive items">
        {state.loading && state.items.length === 0 ? (
          <p className="onedrive-browser-empty">Loading…</p>
        ) : state.items.length === 0 ? (
          <p className="onedrive-browser-empty">
            {state.mode === "search"
              ? "No matches found."
              : "This folder is empty."}
          </p>
        ) : (
          state.items.map((item) => (
            <OneDriveRow
              key={item.id}
              item={item}
              isSelected={selected?.id === item.id}
              onClick={() => navigateInto(item)}
              maxBytes={maxBytes}
            />
          ))
        )}
      </div>

      {state.nextLink ? (
        <div className="onedrive-browser-load-more">
          <Button variant="ghost" onClick={() => void loadMore()} disabled={state.loading}>
            {state.loading ? "Loading…" : "Load more"}
          </Button>
        </div>
      ) : null}

      {selected && !selected.isFolder ? (
        <p className="onedrive-browser-selected">
          Selected: <strong>{selected.name}</strong> · {humanFileSize(selected.size)}
          {typeof selected.size === "number" && selected.size > maxBytes
            ? ` · over the ${humanFileSize(maxBytes)} limit`
            : ""}
        </p>
      ) : null}
    </Modal>
  );
}

// ─── Row component ─────────────────────────────────────────────

function OneDriveRow({
  item,
  isSelected,
  onClick,
  maxBytes,
}: {
  item: OneDriveItem;
  isSelected: boolean;
  onClick: () => void;
  maxBytes: number;
}) {
  const isOversize =
    !item.isFolder && typeof item.size === "number" && item.size > maxBytes;
  return (
    <button
      type="button"
      className={`onedrive-browser-row${isSelected ? " is-selected" : ""}${item.isFolder ? " is-folder" : ""}`}
      onClick={onClick}
      role="option"
      aria-selected={isSelected}
      title={item.parentPath ?? undefined}
    >
      <span className="onedrive-browser-icon" aria-hidden="true">
        {item.isFolder ? (
          <Folder size={16} strokeWidth={1.5} aria-hidden />
        ) : (
          <FileText size={16} strokeWidth={1.5} aria-hidden />
        )}
      </span>
      <span className="onedrive-browser-name">{item.name}</span>
      <span className="onedrive-browser-meta">
        {item.isFolder ? "Folder" : humanFileSize(item.size)}
      </span>
      <span className="onedrive-browser-modified">
        {formatModified(item.lastModifiedAt)}
      </span>
      {isOversize ? (
        <span
          className="onedrive-browser-oversize"
          title={`Over the ${humanFileSize(maxBytes)} limit`}
        >
          Too large
        </span>
      ) : null}
    </button>
  );
}
